import React, { useEffect, useState } from 'react';
import { FiPlus, FiTrash2, FiLink, FiX, FiInfo } from 'react-icons/fi';
import {
  SOCIAL_PLATFORMS,
  CUSTOM_PLATFORM_KEY,
  platformLabel,
  platformExample,
  platformHint,
  platformNote,
  sortEntriesForDisplay,
} from '../utils/socialPlatforms';
import PlatformIcon from './PlatformIcon';
import PaymentNoticeModal from './PaymentNoticeModal';
import { inPaymentGroup } from '../utils/socialPlatformRegistry';
import { bankFromIban } from '../utils/pakistaniBanks';
import './SocialEditor.css';

// Local-only identity for React's key/autoFocus purposes -- has nothing
// to do with the Mongo _id an entry may or may not carry (see toRow).
// Module-scoped counter rather than per-mount state, so ids stay unique
// even if the editor unmounts and remounts (e.g. toggling the Settings
// tab and back) within the same page load.
let localIdSeq = 0;
const makeLocalId = () => `social-row-${localIdSeq++}`;

// An entry from user.socialList (owner shape, includes _id -- see
// buildOwnerSocialList in route/api/auth.js) becomes an editable row.
// _id is preserved as-is, including undefined for an entry that was only
// ever derived from the legacy `social` fallback and never actually
// saved as a socialEntries element -- the server's own rule for "treat
// as new" is exactly "no _id sent", so that has to stay undefined here,
// not get invented.
const toRow = (entry) => ({
  localId: makeLocalId(),
  _id: entry._id,
  platform: entry.platform,
  value: entry.value || '',
  label: entry.label || '',
  autoFocus: false,
});

// Two lines per row, the same shape for every platform including
// custom: an editable title on top (the label -- for a known platform,
// this is an override of its default name; for custom, it's the only
// name it has, "Custom" itself is just what platformLabel('custom')
// falls back to when it's empty) and the value beneath it. Nothing in
// the markup branches on platform anymore, only placeholder text does.
const SocialEditorRow = ({ row, onChange, onRemove }) => {
  const isCustom = row.platform === CUSTOM_PLATFORM_KEY;
  const defaultName = platformLabel(row.platform);
  // Custom has no fixed example/hint of its own (see socialPlatforms.js)
  // -- its value placeholder stays the generic "https://..." it always
  // was, and there's nothing for the info icon to reveal, so it's left
  // off that row entirely rather than showing an empty toggle.
  const valuePlaceholder = isCustom ? 'https://...' : platformExample(row.platform);
  const hint = isCustom ? '' : platformHint(row.platform);
  // Payment platforms only (empty string for every other platform, so
  // this renders unconditionally without a kind check). Unlike `hint`,
  // it is not behind the info toggle: it states a consequence of saving
  // rather than the input format, and a consequence you have to go
  // looking for is not one the person actually saw. Deliberately one
  // line and plainly worded -- an informed choice, not a warning wall
  // that trains people to dismiss it.
  const note = isCustom ? '' : platformNote(row.platform);
  // Resolved live from what has been typed so far, so the bank's logo
  // appearing is itself the confirmation that the IBAN was read the way
  // the profile will read it. Stays null until the value is a complete,
  // structurally valid IBAN (see bankCodeFromIban), so a half-typed one
  // shows the generic icon rather than flickering through whichever
  // banks the leading characters happen to match.
  const bank = row.platform === 'iban' ? bankFromIban(row.value) : null;
  const [showHint, setShowHint] = useState(false);

  return (
    <li className="social-editor__row">
      <div className="social-editor__icon">
        {/* Falls back to a glyph when a platform has no artwork yet --
            the payment platforms don't -- instead of rendering a broken
            image. See PlatformIcon. */}
        <PlatformIcon platform={row.platform} size={28} src={bank ? bank.icon : null} />
      </div>

      <div className="social-editor__fields">
        {/* Placeholder is exactly the platform's default name, so an
            empty title visibly reads as "this is what shows if you
            don't set one" rather than a blank required field -- and
            leaving it empty really does mean no label gets saved (see
            handleSave's trim()), not an empty string stored as one. */}
        <input
          type="text"
          className="social-editor__title"
          placeholder={defaultName}
          value={row.label}
          onChange={(e) => onChange({ label: e.target.value })}
          autoFocus={row.autoFocus && isCustom}
        />
        <input
          type="text"
          className="social-editor__input"
          placeholder={valuePlaceholder}
          value={row.value}
          onChange={(e) => onChange({ value: e.target.value })}
          autoFocus={row.autoFocus && !isCustom}
        />
        {/* Inline and expanding underneath the field, not a floating
            tooltip -- tooltips need hover, which touch doesn't have, and
            compete with tapping the next field for a tap-to-dismiss. */}
        {note && <p className="social-editor__note">{note}</p>}
        {showHint && hint && <p className="social-editor__hint">{hint}</p>}
      </div>

      {/* Info and delete share one row, both centred on the row's full
          height rather than the info icon sitting wherever it happened
          to fall inside the two-line fields column -- with a deliberate
          gap between them (see .social-editor__actions), since they're
          adjacent tap targets and only one of them is reversible. */}
      <div className="social-editor__actions">
        {hint && (
          <button
            type="button"
            className={`social-editor__hint-toggle${
              showHint ? ' social-editor__hint-toggle--active' : ''
            }`}
            aria-label={showHint ? `Hide guidance for ${defaultName}` : `What to enter for ${defaultName}`}
            aria-expanded={showHint}
            onClick={() => setShowHint((v) => !v)}
          >
            <FiInfo />
          </button>
        )}
        <button
          type="button"
          className="social-editor__delete"
          aria-label={`Remove ${row.label || defaultName}`}
          onClick={onRemove}
        >
          <FiTrash2 />
        </button>
      </div>
    </li>
  );
};

// The picker's two groups, split on the registry's `group` -- what a user
// thinks a platform is -- and deliberately not on `kind`, which is what
// the app does with it. Grouping by kind put the heading in the middle of
// the payment methods: PayPal, Venmo, Cash App, Revolut and Wise are
// kind: 'link' (they open a URL, they get no QR and no value validation)
// but every one of them is a way to pay someone, and users read them that
// way. See the PAYMENT METHODS block in socialPlatformRegistry.js.
//
// Still derived, not hand-kept: order and membership both come from the
// registry list, so a platform added with group: 'payment' appears here
// in its list position with nothing else to update.
const LINK_PLATFORMS = SOCIAL_PLATFORMS.filter((p) => !inPaymentGroup(p.key));
const PAYMENT_PLATFORMS = SOCIAL_PLATFORMS.filter((p) => inPaymentGroup(p.key));

// Bottom-sheet platform picker -- position:fixed, same convention
// LeadCaptureModal.js already uses for exactly this reason: EditProfile's
// markup sits several `overflow:hidden` ancestors deep (see NavMenu.js's
// own comment on the same issue), and a fixed-position overlay paints
// relative to the viewport rather than being clipped by any of them, so
// there's no need for a portal to escape that.
const PlatformPicker = ({ onSelect, onClose }) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="social-editor-picker-backdrop" onClick={onClose}>
      <div className="social-editor-picker" onClick={(e) => e.stopPropagation()}>
        <div className="social-editor-picker__header">
          <h4 className="social-editor-picker__title">Add a social</h4>
          <span
            className="social-editor-picker__close"
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="Close"
          >
            <FiX />
          </span>
        </div>
        <ul className="social-editor-picker__list">
          {/* Every known platform, always -- the same platform can be
              added more than once (two phone numbers, two Instagram
              accounts), so nothing here gets filtered out for already
              being in use.

              Split into link platforms and payment platforms so the
              notice below can introduce the payment ones as a group. The
              order within each group is the registry's own, and the
              registry already lists the payment platforms last, so this
              split does not move anything -- it only puts a heading and a
              notice at the seam that was already there. */}
          {LINK_PLATFORMS.map((platform) => (
            <li key={platform.key}>
              <button
                type="button"
                className="social-editor-picker__option"
                onClick={() => onSelect(platform.key)}
              >
                {/* Same glyph fallback as the rows -- the payment
                    platforms appear in this picker before their artwork
                    exists, and a broken image in the add-menu is the
                    first thing a user would see of the feature. */}
                <span className="social-editor-picker__icon">
                  <PlatformIcon platform={platform.key} size={24} />
                </span>
                <span>{platform.label}</span>
              </button>
            </li>
          ))}

          {/* Rendered unconditionally rather than behind the info toggle
              the per-field hints use: this says what not to put in, and
              guidance you have to tap to discover is guidance most people
              never see. It introduces the group rather than repeating on
              every payment row -- the per-entry note (platformNote) still
              carries what is specific to one entry, like the IBAN's
              account-title disclosure.

              PAYMENT_PLATFORMS is never empty in practice, but the guard
              keeps the heading and notice from rendering as a dangling
              section if the registry ever stops listing any. */}
          {PAYMENT_PLATFORMS.length > 0 && (
            <React.Fragment>
              <li className="social-editor-picker__section">
                <h5 className="social-editor-picker__section-title">Payment methods</h5>
              </li>
              {PAYMENT_PLATFORMS.map((platform) => (
                <li key={platform.key}>
                  <button
                    type="button"
                    className="social-editor-picker__option"
                    onClick={() => onSelect(platform.key)}
                  >
                    <span className="social-editor-picker__icon">
                      <PlatformIcon platform={platform.key} size={24} />
                    </span>
                    <span>{platform.label}</span>
                  </button>
                </li>
              ))}
            </React.Fragment>
          )}
          <li>
            <button
              type="button"
              className="social-editor-picker__option social-editor-picker__option--custom"
              onClick={() => onSelect(CUSTOM_PLATFORM_KEY)}
            >
              <FiLink className="social-editor-picker__icon social-editor-picker__icon--glyph" />
              <span>Custom link</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Replaces Views/AddSocialsView.js's fixed 25-slot list for the edit-
// profile flow: only entries the user actually has are rendered, each as
// its own row, added one at a time via the +picker instead of always
// showing every possible platform. initialEntries is the owner's
// socialList (see GET /api/auth/current) -- already sorted by order, and
// already carrying each entry's _id.
const SocialEditor = ({ initialEntries, onSubmit, paymentNoticeAcknowledged, onAcknowledgePaymentNotice }) => {
  const [rows, setRows] = useState(() => (initialEntries || []).map(toRow));
  const [pickerOpen, setPickerOpen] = useState(false);
  // Seeded from the server-stored flag (see paymentNoticeAckedAt in
  // models/User.js) and then held locally, so acknowledging closes the
  // modal immediately without waiting on the request or a refetch.
  const [noticeAcked, setNoticeAcked] = useState(!!paymentNoticeAcknowledged);
  const [noticeOpen, setNoticeOpen] = useState(false);

  const updateRow = (localId, patch) => {
    setRows((prev) => prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row)));
  };

  const removeRow = (localId) => {
    setRows((prev) => prev.filter((row) => row.localId !== localId));
  };

  // New entries append to the end -- no reordering in this pass (that's
  // a separate, later piece of work, presumably drag-and-drop). The
  // server re-numbers `order` from array position on save regardless of
  // what's sent, so this local array order is what actually determines
  // the saved order.
  const addRow = (platform) => {
    setRows((prev) => [
      ...prev,
      { localId: makeLocalId(), _id: undefined, platform, value: '', label: '', autoFocus: true },
    ]);
    setPickerOpen(false);
    // The row is added either way -- the notice explains what not to type
    // into it, so it belongs after the field exists, not as a gate in
    // front of it. Keyed on the group (what the user is doing) rather
    // than the kind, so adding a Wise handle prompts exactly as adding an
    // IBAN does.
    if (inPaymentGroup(platform) && !noticeAcked) setNoticeOpen(true);
  };

  const acknowledgeNotice = () => {
    setNoticeOpen(false);
    setNoticeAcked(true);
    if (onAcknowledgePaymentNotice) onAcknowledgePaymentNotice();
  };

  const handleSave = () => {
    // Rows with an empty value are dropped here, not saved as blanks --
    // e.g. a platform added via the picker and then not filled in, or
    // emptied back out by hand.
    const cleaned = rows
      .map((row) => ({
        _id: row._id,
        platform: row.platform,
        value: row.value.trim(),
        label: row.label.trim(),
      }))
      .filter((entry) => entry.value !== '');
    onSubmit(cleaned);
  };

  return (
    <div className="social-editor">
      <form onSubmit={(e) => e.preventDefault()}>
        <ul className="social-editor__list">
          {/* Rendered in the order a visitor sees (payment methods last,
              see sortEntriesForDisplay), so the editor is not showing a
              different arrangement from the profile it edits.

              Only the rendering is sorted. `rows` itself stays in the
              user's stored order and is what handleSave sends, because
              the server re-numbers `order` from array position -- saving
              the sorted view would quietly rewrite the stored order this
              is deliberately not touching. Row edits are keyed by
              localId, so operating on a reordered view is safe. */}
          {sortEntriesForDisplay(rows).map((row) => (
            <SocialEditorRow
              key={row.localId}
              row={row}
              onChange={(patch) => updateRow(row.localId, patch)}
              onRemove={() => removeRow(row.localId)}
            />
          ))}
        </ul>

        {/* Stays available after every addition -- it's outside the row
            list, not replaced or hidden by anything the rows do. */}
        <button
          type="button"
          className="social-editor__add-btn"
          onClick={() => setPickerOpen(true)}
        >
          <FiPlus /> Add social
        </button>

        <button
          type="button"
          className="btn w-100prc social-editor__save-btn"
          onClick={handleSave}
        >
          Save
        </button>
      </form>

      {pickerOpen && <PlatformPicker onSelect={addRow} onClose={() => setPickerOpen(false)} />}
      {/* Owner-facing: shown the first time they add a payment method,
          and never again once acknowledged. A modal rather than the
          inline notice it replaced, because sitting in the picker it was
          read at the moment a user was scanning for a platform name --
          the moment they are least likely to read prose -- and it was as
          much in the way of the tenth entry as the first. */}
      {noticeOpen && (
        <PaymentNoticeModal
          title={
            <React.Fragment>
              <span className="payment-notice__beta">Beta</span> Payment methods are new
            </React.Fragment>
          }
          buttonLabel="I understand"
          onAcknowledge={acknowledgeNotice}
        >
          Only add details people need to send you money: IBAN, Raast ID, wallet number,
          or your payment handle. Never enter your bank password, PIN, OTP, CVV, full
          card number or CNIC. Everything you add here is publicly visible on your
          profile.
        </PaymentNoticeModal>
      )}
    </div>
  );
};

export default SocialEditor;
